<template>
  <div v-headroom class="headroom-wrapper">
    <header ref="pageHeader" class="page-header flex flex-col">
      <section class="secondary flex justify-between align-center text-center">
        <NavigationElementContact />
        <div class="page-header__tools">
          <NavigationElementTools />
          <NavigationElementQuickExit :is-global="true" />
        </div>
      </section>
      <section class="primary">
        <NavigationElementBrand class="brand--small" />
        <NavigationElementHeaderMenu class="hidden-xs hidden-sm" />
        <div class="page-header__search flex align-center justify-end">
          <WeglotLanguageSwitcher class="hidden-lg hidden-xl" />
          <NavigationElementSearchForm v-if="!isSearchPage" />
          <div ref="quickExit">
            <NavigationElementQuickExit is-compact />
          </div>
        </div>
      </section>
    </header>
  </div>
</template>

<script setup lang="ts">
const emit = defineEmits(['onPageHeaderHeight'])
const pageHeader = ref<HTMLElement | null>(null)

const route = useRoute()
const isSearchPage = computed(() => route.path === '/search/')

function getHeaderHeight() {
  if (!pageHeader.value) return
  const height = pageHeader.value.offsetHeight
  emit('onPageHeaderHeight', height)
}

useEventListener('resize', getHeaderHeight)
onMounted(() => {
  nextTick(() => {
    getHeaderHeight()
  })
})
</script>

<style lang="less" scoped>
header.page-header {
  .fixed();

  z-index: 9997;
  top: 0;
  left: 0;
  width: 100vw;
  transition: transform 0.3s ease-out 0.4s;
  max-height: -webkit-fill-available;

  > section {
    background: @color-fill-normal;
    .flex-col();

    @media @wide {
      .flex-row();
    }

    &.secondary {
      .hidden();
      // .absolute();
      .flex-auto();

      order: 999;
      width: 100%;
      // bottom: 0;
      // z-index: 11;
      box-shadow: inset 0 @1px @1px 0 @color-border;

      @media @wide {
        .flex();
        .static();

        bottom: auto;
        max-height: @desktop-nav-secondary-height;
        order: 0;
        box-shadow: inset 0 -@1px @1px 0 @color-border;
        padding: 0 0 0 @gutter-lg;
      }

      .page-header__tools {
        .hidden();

        @media @wide {
          .flex();

          width: auto;
        }
      }
    }

    &.primary {
      .flex();
      .relative();
      .align-center();
      .flex-auto-fill();

      width: 100%;
      overflow: hidden;

      @media @tablet {
        .align-center();

        box-shadow: inset 0 -@1px @1px 0 @color-border;
      }

      @media @wide {
        overflow: visible;
        .justify-between();
      }

      .brand,
      .page-header__search {
        @media @wide {
          width: @280px;
        }
      }

      .brand {
        align-self: flex-start;
        margin-left: @gutter;

        @media @wide {
          align-self: auto;
          margin: 0;
          padding-left: @gutter-lg;
        }
      }

      nav {
        .hidden();

        width: 100%;
        overflow-y: scroll;

        @media @wide {
          .flex();

          width: auto;
          order: initial;
          overflow-y: visible;
        }
      }

      .page-header__search {
        .absolute();

        top: 0;
        right: 0;

        @media @wide {
          .relative();

          top: auto;
          right: auto;
          flex: 0 0 auto;
          transform: translate(@66px, 0);
          transition: transform 0.3s ease-in-out;
        }

        .search-form {
          .hidden();

          width: @195px;
          margin: 0 @gutter 0 @gutter-lg;

          @media @wide {
            display: block;
            width: 100%;
            margin-left: 0;
          }
        }
      }
    }
  }
}

// headroom states

.headroom-wrapper {
  height: 0 !important;

  @media print {
    height: auto !important;
  }
}

.headroom-page-header {
  &.headroom--not-top {
    header.page-header {
      @media @wide {
        transform: translate(0, -@desktop-nav-secondary-height);
        transition: transform 0.4s cubic-bezier(0.25, 0, 0.66, 1);

        .page-header__search {
          transform: translate(0, 0);
          transition: transform 0.6s ease-in-out 0.6s;

          a.button--secondary {
            width: @66px;
          }
        }
      }
      :deep(.language-switcher .list) {
        @media @wide {
          height: 0;
        }
      }
    }
  }
}

@media print {
  header.page-header {
    .flex();
    .relative();
    .flex-row();
    .align-center();

    > section {
      &.secondary {
        .block();
        .flex-grow();

        box-shadow: none;

        .page-header__tools {
          .hidden();
        }

        :deep(.contact) {
          .flex();
          .flex-row();
          .justify-end();

          padding: 0;
          border: none;

          a.emergency {
            .hidden();
          }

          > * {
            .block();
          }
        }
      }

      &.primary {
        flex: 0 1 auto;

        nav,
        .page-header__search {
          .hidden();
        }

        .brand {
          margin-left: 0 !important;
        }
      }

      > .search--page-header {
        .hidden();
      }
    }
  }
}
</style>
